import React, { useState, useEffect } from "react";
import axios from "axios";
import ResultReport from "./ResultsReport";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faSave, faDownload, faPrint, faUpload } from "@fortawesome/free-solid-svg-icons";
import { exportToExcel } from "../export/exportToExcel";
import { exportToPdf } from "../export/exportToPdf";
import { exportToWord } from "../export/exportToWord";
import { exportToPrint } from "../export/exportToPrint";

const MainReport = () => {
  const [showResult, setShowResult] = useState(false);
  const [selectedClass, setSelectedClass] = useState("");
  const [selectedSection, setSelectedSection] = useState("");
  const [selectedTest, setSelectedTest] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [classSectionData, setClassSectionData] = useState([]);
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDropdownIndex, setOpenDropdownIndex] = useState(null);

  useEffect(() => {
    const handlePopState = () => setShowResult(false);
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      const userRole = localStorage.getItem("userRole");
      if (!userRole) {
        console.warn("No userRole found in localStorage. Please log in.");
        return;
      }

      try {
        setLoading(true);
        const classRes = await axios.get("https://aadyaacademy.org/api/classes-sections");
        if (Array.isArray(classRes.data.data)) {
          setClassSectionData(classRes.data.data);
        }
        const testRes = await axios.get("https://aadyaacademy.org/api/tests");
        if (Array.isArray(testRes.data.data)) {
          setTests(testRes.data.data);
          if (testRes.data.data.length > 0) {
            setSelectedTest(testRes.data.data[0].name);
          }
        }
      } catch (error) {
        console.error("Fetch Error:", error);
        setClassSectionData([]);
        setTests([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleViewClick = (cls, sec) => {
    setSelectedClass(cls);
    setSelectedSection(sec);
    setShowResult(true);
    window.history.pushState({}, "", "");
  };

  const handleBackClick = () => {
    setShowResult(false);
    setSelectedClass("");
    setSelectedSection("");
    setSearchTerm("");
  };

  const handleSave = (cls, sec) => {
    alert(`Saved result report for Class ${cls} - Section ${sec}`);
  };

  const handleDownload = (index) => {
    setOpenDropdownIndex(openDropdownIndex === index ? null : index);
  };

  const transformStudentData = (student) => {
    const scholastic = student.scholastic_marks || {};
    const co_scholastic = student.co_scholastic_marks || {};
    const observation = student.traitRemarks || {};
    const remarks = student.remarks || "";
    const maxMarks = student.max_marks || {};

    const grandTotal = Object.values(scholastic).reduce(
      (sum, val) => sum + (Number(val) || 0),
      0
    );

    return {
      name: student.student_name,
      student_id: student.student_id,
      class: student.class,
      section: student.section,
      scholastic,
      co_scholastic,
      observation,
      remarks,
      grandTotal,
      max_marks: maxMarks,
    };
  };

  const downloadAs = async (format, cls, sec) => {
    const fileName = `Unit_Test_Report_${cls}_${sec}`;
    try {
      setLoading(true);
      const res = await axios.get(
        `https://aadyaacademy.org/api/class_marks?class=${cls}&section=${sec}&test=${selectedTest}`
      );

      const studentRecords = Array.isArray(res.data?.data)
        ? res.data.data.map(transformStudentData)
        : [];

      if (!studentRecords.length) {
        alert("No student records found.");
        return;
      }

      if (format === "excel") exportToExcel(studentRecords, fileName, selectedTest); // ✅ Pass selectedTest
      else if (format === "pdf") await exportToPdf(studentRecords, fileName);
      else if (format === "word") await exportToWord(studentRecords, fileName);
      else if (format === "print") exportToPrint(studentRecords, cls, sec);
    } catch (err) {
      console.error("Download error:", err.response?.data || err.message);
      alert("Failed to fetch student data for export.");
    } finally {
      setOpenDropdownIndex(null);
      setLoading(false);
    }
  };

const handleUpload = async (e, cls, sec) => {
  const file = e.target.files[0];
  if (!file) return;

  // Log original file info
  console.log("Uploaded File:", {
    name: file.name,
    type: file.type,
    size: `${(file.size / 1024).toFixed(2)} KB`,
  });

  // Ensure the file has a proper .xlsx or .csv extension
  const extensionMatch = file.name.match(/\.(xls|xlsx|csv)$/i);
  const safeFileName = extensionMatch ? file.name : "upload.xlsx";

  const uploadFile = new File([file], safeFileName, {
    type: file.type || "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });

  const formData = new FormData();
  formData.append("file", uploadFile, safeFileName);
  formData.append("class", cls);
  formData.append("section", sec);
  formData.append("test", selectedTest);

  console.log("Sending file to server:", safeFileName);

  try {
    setLoading(true);
    const res = await axios.post(
      "https://aadyaacademy.org/api/admin/upload_excel",
      formData,
      {
        headers: { "Content-Type": "multipart/form-data" },
      }
    );
    alert(res.data.message || "File uploaded successfully!");
  } catch (err) {
    console.error("❌ Upload error:", err.response?.data || err.message);
    alert(err.response?.data?.message || "Failed to upload file.");
  } finally {
    setLoading(false);
    e.target.value = null; // Reset input
  }
};


  const getAllClasses = () => [...new Set(classSectionData.map((item) => item.class))];
  const getAllSections = () => [...new Set(classSectionData.flatMap((item) => item.sections))];

  const filteredResults = [];
  classSectionData.forEach((clsObj) => {
    const clsName = clsObj.class;
    clsObj.sections.forEach((sec) => {
      const matchSearch =
        clsName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        sec.toLowerCase().includes(searchTerm.toLowerCase());
      const matchClass = selectedClass === "" || selectedClass === clsName;
      const matchSection = selectedSection === "" || selectedSection === sec;
      if (matchSearch && matchClass && matchSection) {
        filteredResults.push({ class: clsName, section: sec });
      }
    });
  });

  if (showResult) {
    return (
      <ResultReport
        selectedClass={selectedClass}
        selectedSection={selectedSection}
        selectedTest={selectedTest}
        onBack={handleBackClick}
      />
    );
  }

  const buttonStyle = {
    backgroundColor: "#e0e0e0",
    border: "none",
    borderRadius: "8px",
    padding: "8px 10px",
    fontSize: "12px",
    cursor: "pointer",
  };

  const dropdownItemStyle = {
    padding: "6px 10px",
    cursor: "pointer",
    borderBottom: "1px solid #eee",
    fontSize: "12px",
    backgroundColor: "#fff",
  };

  return (
    <div className="assignment-report" style={{ fontSize: "13px" }}>
      <div className="table-header" style={{ marginBottom: "10px" }}>
        <h3 style={{ fontSize: "16px" }}>Results Report</h3>
       <div className="table-header-btm" style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
  <input
    type="text"
    placeholder="Search by class or section"
    value={searchTerm}
    onChange={(e) => setSearchTerm(e.target.value)}
    style={{
      height: "50px",
      border: "none",
      width: "130px",
      fontSize: "12px",
      padding: "8px 5px",
      borderRadius: "10px",
      backgroundColor: "rgba(0,0,0,0.086)",
      outline: "none",
    }}
  />
  <select
    value={selectedClass}
    onChange={(e) => setSelectedClass(e.target.value)}
    style={{
      height: "50px",
      border: "none",
      width: "130px",
      fontSize: "12px",
      padding: "8px 5px",
      borderRadius: "10px",
      backgroundColor: "rgba(0,0,0,0.086)",
      outline: "none",
    }}
  >
    <option value="">Class</option>
    {getAllClasses().map((cls, i) => (
      <option key={i} value={cls}>{cls}</option>
    ))}
  </select>
  <select
    value={selectedSection}
    onChange={(e) => setSelectedSection(e.target.value)}
    style={{
      height: "50px",
      border: "none",
      width: "130px",
      fontSize: "12px",
      padding: "8px 5px",
      borderRadius: "10px",
      backgroundColor: "rgba(0,0,0,0.086)",
      outline: "none",
    }}
  >
    <option value="">Section</option>
    {getAllSections().map((sec, i) => (
      <option key={i} value={sec}>{sec}</option>
    ))}
  </select>
  <select
    value={selectedTest}
    onChange={(e) => setSelectedTest(e.target.value)}
    style={{
      height: "50px",
      border: "none",
      width: "130px",
      fontSize: "12px",
      padding: "8px 5px",
      borderRadius: "10px",
      backgroundColor: "rgba(0,0,0,0.086)",
      outline: "none",
    }}
  >
    {tests.map((test) => (
      <option key={test.id} value={test.name}>{test.name}</option>
    ))}
  </select>
</div>

      </div>

      {loading ? (
        <p style={{ fontSize: "12px" }}>Loading...</p>
      ) : (
        <table style={{ fontSize: "12px", width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th>Class</th>
              <th>Section</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {filteredResults.length > 0 ? (
              filteredResults.map((item, index) => (
                <tr key={index}>
                  <td>{item.class}</td>
                  <td>{item.section}</td>
                  <td style={{ position: "relative" }}>
                    <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                      <button
                        onClick={() => handleViewClick(item.class, item.section)}
                        title="View"
                        style={buttonStyle}
                      >
                        <FontAwesomeIcon icon={faEye} />
                      </button>
                      <button
                        onClick={() => handleSave(item.class, item.section)}
                        title="Save"
                        style={buttonStyle}
                      >
                        <FontAwesomeIcon icon={faSave} />
                      </button>
                      <button
                        onClick={() => downloadAs("print", item.class, item.section)}
                        title="Print"
                        style={buttonStyle}
                      >
                        <FontAwesomeIcon icon={faPrint} />
                      </button>
                      <button
                        onClick={() => handleDownload(index)}
                        title="Download"
                        style={buttonStyle}
                      >
                        <FontAwesomeIcon icon={faDownload} />
                      </button>
                      <div style={{ position: "relative" }}>
                        <button
                          onClick={() => document.getElementById(`uploadInput-${index}`).click()}
                          title="Upload"
                          style={buttonStyle}
                        >
                          <FontAwesomeIcon icon={faUpload} />
                        </button>
                        <input
                          type="file"
                          id={`uploadInput-${index}`}
                          style={{ display: "none" }}
                          onChange={(e) => handleUpload(e, item.class, item.section)}
                        />
                        {openDropdownIndex === index && (
                          <div
                            style={{
                              position: "absolute",
                              top: "40px",
                              right: 0,
                              backgroundColor: "#fff",
                              border: "1px solid #ccc",
                              borderRadius: "5px",
                              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                              zIndex: 1000,
                            }}
                          >
                            <div
                              onClick={() => downloadAs("excel", item.class, item.section)}
                              style={dropdownItemStyle}
                            >
                              Excel
                            </div>
                            <div
                              onClick={() => downloadAs("word", item.class, item.section)}
                              style={dropdownItemStyle}
                            >
                              Word
                            </div>
                            <div
                              onClick={() => downloadAs("pdf", item.class, item.section)}
                              style={{ ...dropdownItemStyle, borderBottom: "none" }}
                            >
                              PDF
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="3" style={{ textAlign: "center", fontSize: "12px" }}>
                  No records found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default MainReport;
